/**
 * NewConversationModal Component
 * Modal for creating a new conversation with a tenant
 */

import React, { useEffect, useState } from "react";
import { CreateConversationRequest } from "../../../store/api/conversationsApi";
import { useListUsersQuery } from "../../../store/api/usersApi";

interface NewConversationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreateConversation: (data: CreateConversationRequest) => void;
	isCreating: boolean;
	organizationId: string;
}

export const NewConversationModal: React.FC<NewConversationModalProps> = ({
	isOpen,
	onClose,
	onCreateConversation,
	isCreating,
	organizationId,
}) => {
	const [subject, setSubject] = useState("");
	const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");

	// Fetch tenants only (role=tenant)
	const { data: usersResponse } = useListUsersQuery({
		role: "tenant",
		page_size: 1000,
	});
	const tenants = usersResponse?.results || [];

	// Filter tenants by search query
	const filteredTenants = tenants.filter(
		(tenant) =>
			tenant.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			tenant.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			tenant.email?.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setSubject("");
			setSelectedTenantId(null);
			setSearchQuery("");
		}
	}, [isOpen]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (subject.trim() && selectedTenantId) {
			onCreateConversation({
				organization: organizationId,
				participants: [selectedTenantId],
				subject: subject.trim(),
			});
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
			<div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b border-border">
					<h2 className="text-xl font-semibold text-foreground">
						New Conversation with Tenant
					</h2>
					<button
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground transition-colors"
						disabled={isCreating}
					>
						<svg
							className="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="flex flex-col h-full">
					<div className="flex-1 overflow-y-auto p-6 space-y-6">
						{/* Subject */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Subject *
							</label>
							<input
								type="text"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								placeholder="e.g., Property Inquiry, Lease Question, Maintenance Request"
								required
								disabled={isCreating}
								className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground disabled:bg-muted disabled:cursor-not-allowed"
							/>
						</div>

						{/* Tenant Selection */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Select Tenant *
							</label>

							{/* Search */}
							<div className="relative mb-3">
								<input
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search tenants by name or email..."
									disabled={isCreating}
									className="w-full px-4 py-2 pl-10 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground disabled:bg-muted disabled:cursor-not-allowed"
								/>
								<svg
									className="absolute left-3 top-2.5 w-5 h-5 text-muted-foreground"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
									/>
								</svg>
							</div>

							{/* Tenant List */}
							<div className="border border-border rounded-lg max-h-64 overflow-y-auto bg-background">
								{filteredTenants.length === 0 ? (
									<div className="p-4 text-center text-sm text-muted-foreground">
										{searchQuery
											? "No tenants found matching your search"
											: "No tenants available"}
									</div>
								) : (
									filteredTenants.map((tenant) => {
										const isSelected = selectedTenantId === tenant.id;
										const fullName =
											`${tenant.first_name || ""} ${
												tenant.last_name || ""
											}`.trim() || tenant.email;

										return (
											<button
												key={tenant.id}
												type="button"
												onClick={() => setSelectedTenantId(tenant.id)}
												disabled={isCreating}
												className={`w-full px-4 py-3 text-left border-b border-border last:border-b-0 hover:bg-muted/50 transition-colors ${
													isSelected
														? "bg-primary/10 border-l-4 border-l-primary"
														: ""
												} disabled:cursor-not-allowed`}
											>
												<div className="flex items-center justify-between">
													<div className="flex-1 min-w-0">
														<div className="font-medium text-foreground truncate">
															{fullName}
														</div>
														<div className="text-sm text-muted-foreground truncate">
															{tenant.email}
														</div>
														{tenant.phone && (
															<div className="text-xs text-muted-foreground">
																{tenant.phone}
															</div>
														)}
													</div>
													{isSelected && (
														<svg
															className="w-5 h-5 text-primary flex-shrink-0 ml-2"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M5 13l4 4L19 7"
															/>
														</svg>
													)}
												</div>
											</button>
										);
									})
								)}
							</div>
						</div>
					</div>

					{/* Footer */}
					<div className="p-6 border-t border-border bg-muted/20 flex items-center justify-end space-x-3">
						<button
							type="button"
							onClick={onClose}
							disabled={isCreating}
							className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={!subject.trim() || !selectedTenantId || isCreating}
							className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isCreating ? "Creating..." : "Create Conversation"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
